import React, { useState, useEffect, useMemo } from "react";
import API from "../api/axiosConfig";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag, Plus, Minus, Trash2, Send, Package,
  Tag, ChevronDown, ChevronUp, X, Check, Loader2,
  UtensilsCrossed, Store,
} from "lucide-react";
import { getImageUrl } from "../utils/getImageUrl";
import { isFoodCategory } from "../utils/isRestaurantCategory";

// ─── Product card ─────────────────────────────────────────────────────────────
function ProductCard({ product, qty, onAdd, onRemove }) {
  const img = Array.isArray(product.images) ? product.images[0] : product.images;
  const hasOffer = product.offerPrice && Number(product.offerPrice) < Number(product.price);
  const displayPrice = hasOffer ? Number(product.offerPrice) : Number(product.price);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition"
    >
      {/* Image */}
      <div className="relative h-28 bg-gray-50">
        {img
          ? <img src={getImageUrl(img)} alt={product.title} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center">
              <UtensilsCrossed size={32} className="text-gray-200" />
            </div>
        }
        {hasOffer && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full">
            -{Math.round(((product.price - product.offerPrice) / product.price) * 100)}%
          </span>
        )}
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="font-bold text-sm text-gray-800 leading-tight line-clamp-2">{product.title}</p>
        <div className="flex items-baseline gap-1.5 mt-1">
          <span className="text-base font-black text-supernova-600">
            ${displayPrice.toLocaleString("es-CO")}
          </span>
          {hasOffer && (
            <span className="text-xs text-gray-400 line-through">
              ${Number(product.price).toLocaleString("es-CO")}
            </span>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="px-3 pb-3">
        {qty === 0 ? (
          <button
            onClick={() => onAdd(product)}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-supernova-500 hover:bg-supernova-600 text-white text-sm font-bold transition"
          >
            <Plus size={14} /> Agregar
          </button>
        ) : (
          <div className="flex items-center justify-between bg-supernova-50 rounded-xl px-2 py-1.5">
            <button
              onClick={() => onRemove(product.id)}
              className="w-7 h-7 flex items-center justify-center rounded-lg bg-supernova-100 hover:bg-supernova-200 text-supernova-700 transition"
            >
              <Minus size={13} />
            </button>
            <span className="font-black text-supernova-700 text-base w-6 text-center">{qty}</span>
            <button
              onClick={() => onAdd(product)}
              className="w-7 h-7 flex items-center justify-center rounded-lg bg-supernova-500 hover:bg-supernova-600 text-white transition"
            >
              <Plus size={13} />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── Order summary drawer ─────────────────────────────────────────────────────
function OrderDrawer({ items, onRemove, onAdd, onRemoveOne, onClear, onSubmit, submitting, companyId }) {
  const [notes, setNotes] = useState("");
  const [open, setOpen] = useState(false);

  const total = items.reduce((sum, it) => {
    const price = it.product.offerPrice && Number(it.product.offerPrice) < Number(it.product.price)
      ? Number(it.product.offerPrice)
      : Number(it.product.price);
    return sum + price * it.qty;
  }, 0);

  const count = items.reduce((s, it) => s + it.qty, 0);

  const handleSubmit = () => {
    onSubmit(notes);
    setNotes("");
    setOpen(false);
  };

  return (
    <>
      {/* Floating button */}
      {count > 0 && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-3 bg-gray-900 hover:bg-gray-800 text-white pl-4 pr-5 py-3.5 rounded-2xl shadow-2xl transition"
        >
          <div className="relative">
            <ShoppingBag size={20} />
            <span className="absolute -top-2 -right-2 w-5 h-5 bg-supernova-500 text-white text-[10px] font-black rounded-full flex items-center justify-center">
              {count}
            </span>
          </div>
          <div className="text-left">
            <p className="text-xs text-gray-400 leading-none">Ver comanda</p>
            <p className="text-sm font-black leading-tight">${total.toLocaleString("es-CO")}</p>
          </div>
        </motion.button>
      )}

      {/* Drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
            <motion.div
              className="relative bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[85vh] flex flex-col shadow-2xl"
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ type: "spring", damping: 22 }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div>
                  <h3 className="font-black text-gray-900 text-base">Comanda</h3>
                  <p className="text-xs text-gray-400">{count} ítem{count !== 1 ? "s" : ""}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={onClear}
                    className="text-xs text-red-400 hover:text-red-600 px-2.5 py-1.5 rounded-lg hover:bg-red-50 transition"
                  >
                    Limpiar
                  </button>
                  <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100 transition">
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Items */}
              <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
                {items.map((it) => {
                  const price = it.product.offerPrice && Number(it.product.offerPrice) < Number(it.product.price)
                    ? Number(it.product.offerPrice) : Number(it.product.price);
                  return (
                    <div key={it.product.id} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-800 truncate">{it.product.title}</p>
                        <p className="text-xs text-supernova-600 font-bold">${(price * it.qty).toLocaleString("es-CO")}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          onClick={() => onRemoveOne(it.product.id)}
                          className="w-6 h-6 flex items-center justify-center rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 transition"
                        >
                          <Minus size={11} />
                        </button>
                        <span className="font-black text-sm w-5 text-center">{it.qty}</span>
                        <button
                          onClick={() => onAdd(it.product)}
                          className="w-6 h-6 flex items-center justify-center rounded-lg bg-supernova-100 hover:bg-supernova-200 text-supernova-700 transition"
                        >
                          <Plus size={11} />
                        </button>
                        <button
                          onClick={() => onRemove(it.product.id)}
                          className="w-6 h-6 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-400 transition ml-1"
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Notes */}
                <div className="pt-2">
                  <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Notas del pedido</label>
                  <textarea
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 resize-none"
                    rows={2}
                    placeholder="Instrucciones especiales, alergias, etc..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 rounded-b-3xl">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-600 font-medium">Total</span>
                  <span className="text-xl font-black text-gray-900">${total.toLocaleString("es-CO")}</span>
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || items.length === 0}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-supernova-500 hover:bg-supernova-600 text-white font-black text-base transition disabled:opacity-60"
                >
                  {submitting
                    ? <><Loader2 size={18} className="animate-spin" /> Enviando...</>
                    : <><Send size={16} /> Enviar comanda</>
                  }
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function EmployeeComanda() {
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  }, []);

  const [company, setCompany] = useState(null);
  const [secciones, setSecciones] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(true);

  const [cart, setCart] = useState([]); // [{ product, qty }]
  const [submitting, setSubmitting] = useState(false);

  const [activeSection, setActiveSection] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    if (!user?.companyId) { setAllowed(false); setLoading(false); return; }
    const load = async () => {
      try {
        const [resCompany, resSec, resProd] = await Promise.allSettled([
          API.get(`/companies/by-id/${user.companyId}`),
          API.get(`/secciones/company/${user.companyId}`),
          API.get(`/products/company/${user.companyId}`),
        ]);
        const co = resCompany.status === "fulfilled"
          ? (resCompany.value.data?.data?.company ?? null)
          : null;
        if (co && !isFoodCategory(co.category)) {
          setAllowed(false);
          setLoading(false);
          return;
        }
        setCompany(co);
        const secs = resSec.status === "fulfilled"
          ? (resSec.value.data?.data?.secciones ?? resSec.value.data?.data ?? [])
          : [];
        setSecciones(Array.isArray(secs) ? secs : []);
        const prods = resProd.status === "fulfilled"
          ? (resProd.value.data?.data?.products ?? resProd.value.data?.data ?? [])
          : [];
        const visible = (Array.isArray(prods) ? prods : []).filter((p) => p.visible !== false);
        setProducts(visible);
      } catch {
        toast.error("Error al cargar datos");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  // Group products by section
  const grouped = useMemo(() => {
    const map = {};
    const sinSeccion = [];
    products.forEach((p) => {
      const sid = p.seccionId ?? p.seccion?.id ?? null;
      if (sid) {
        if (!map[sid]) map[sid] = [];
        map[sid].push(p);
      } else {
        sinSeccion.push(p);
      }
    });
    const sections = secciones
      .filter((s) => map[s.id]?.length)
      .map((s) => ({ id: s.id, name: s.name, products: map[s.id] }));
    if (sinSeccion.length) sections.push({ id: "sin-seccion", name: "Otros", products: sinSeccion });
    return sections;
  }, [products, secciones]);

  const cartMap = useMemo(() => {
    const m = {};
    cart.forEach((it) => { m[it.product.id] = it.qty; });
    return m;
  }, [cart]);

  const addToCart = (product) => {
    setCart((prev) => {
      const idx = prev.findIndex((it) => it.product.id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        return next;
      }
      return [...prev, { product, qty: 1 }];
    });
  };

  const removeOneFromCart = (productId) => {
    setCart((prev) => {
      const idx = prev.findIndex((it) => it.product.id === productId);
      if (idx < 0) return prev;
      if (prev[idx].qty <= 1) return prev.filter((_, i) => i !== idx);
      const next = [...prev];
      next[idx] = { ...next[idx], qty: next[idx].qty - 1 };
      return next;
    });
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((it) => it.product.id !== productId));
  };

  const handleSubmit = async (notes) => {
    if (cart.length === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        companyId: user.companyId,
        deliveryType: "recoger_en_tienda",
        items: cart.map((it) => ({ productId: it.product.id, quantity: it.qty })),
        ...(notes ? { notes } : {}),
      };
      await API.post("/orders", payload);
      toast.success("¡Comanda enviada exitosamente!");
      setCart([]);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al enviar la comanda");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-supernova-500" />
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
          <Store size={32} className="text-gray-300" />
        </div>
        <h2 className="text-xl font-black text-gray-700">Acceso no disponible</h2>
        <p className="text-sm text-gray-400 max-w-xs">
          El sistema de comandas solo está disponible para empleados de restaurantes y tiendas de comida.
          {!user?.companyId && " Tu cuenta no está vinculada a ninguna empresa."}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          {company?.logo && (
            <img src={getImageUrl(company.logo)} alt={company.name} className="w-9 h-9 rounded-xl object-cover border border-gray-100" />
          )}
          <div>
            <h1 className="font-black text-gray-900 text-base leading-tight">
              {company?.name ?? "Comanda"}
            </h1>
            <p className="text-xs text-gray-400">Sistema de pedidos · {user?.name}</p>
          </div>
        </div>

        {/* Section filter pills */}
        {grouped.length > 1 && (
          <div className="overflow-x-auto no-scrollbar px-4 pb-3">
            <div className="flex gap-2 w-max">
              <button
                onClick={() => setActiveSection(null)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition ${
                  activeSection === null
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Todos
              </button>
              {grouped.map((sec) => (
                <button
                  key={sec.id}
                  onClick={() => setActiveSection(sec.id === activeSection ? null : sec.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition ${
                    activeSection === sec.id
                      ? "bg-supernova-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {sec.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-5 space-y-8">
        {grouped.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-300">
            <Package size={48} className="mb-3 opacity-40" />
            <p className="text-sm text-gray-400">No hay productos disponibles</p>
          </div>
        ) : (
          grouped
            .filter((sec) => !activeSection || sec.id === activeSection)
            .map((sec) => {
              const isExpanded = expandedSections[sec.id] !== false;
              return (
                <section key={sec.id}>
                  <button
                    onClick={() => setExpandedSections((prev) => ({ ...prev, [sec.id]: !isExpanded }))}
                    className="flex items-center justify-between w-full mb-3 group"
                  >
                    <div className="flex items-center gap-2">
                      <Tag size={13} className="text-supernova-500" />
                      <h2 className="font-black text-gray-800 text-base">{sec.name}</h2>
                      <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-bold">
                        {sec.products.length}
                      </span>
                    </div>
                    {isExpanded
                      ? <ChevronUp size={16} className="text-gray-400" />
                      : <ChevronDown size={16} className="text-gray-400" />
                    }
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
                      >
                        {sec.products.map((p) => (
                          <ProductCard
                            key={p.id}
                            product={p}
                            qty={cartMap[p.id] ?? 0}
                            onAdd={addToCart}
                            onRemove={removeOneFromCart}
                          />
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </section>
              );
            })
        )}
      </div>

      <OrderDrawer
        items={cart}
        onAdd={addToCart}
        onRemoveOne={removeOneFromCart}
        onRemove={removeFromCart}
        onClear={() => setCart([])}
        onSubmit={handleSubmit}
        submitting={submitting}
        companyId={user?.companyId}
      />
    </div>
  );
}
